import { Button } from "../../button";
import { Icon } from "../../icons";
import { cn } from "../../utils";
import { useDataTableContext } from "./context";
import type { RowData } from "@tanstack/react-table";
import type { Row } from "./types";

/** Props Interface */
interface DataTableGroupHeaderProps<T extends RowData> {
	row: Row<T>;
	renderGroupHeader?: (row: Row<T>) => React.ReactNode;
}

export function DataTableGroupHeader<T extends RowData>({
	row,
	renderGroupHeader,
}: DataTableGroupHeaderProps<T>) {
	/** Hooks */
	const groupingColumnId = row.groupingColumnId;
	const { table, showGroupingHeader } = useDataTableContext();
	const groupingColumns = table.state.grouping;
	const groupingLevel = groupingColumns.indexOf(groupingColumnId!);

	if (!showGroupingHeader) return null;

	/** Render */
	return (
		<div
			className={cn(
				"bg-primary-50! dark:bg-primary-950!",
				"py-2! **:text-xs **:tabular-nums",
				"h-stack px-3",
				"border-b-[0.5px] border-color",
			)}
			key={row.id}
			style={{
				position: "sticky",
				left: 0,
				zIndex: 10,
				filter: `brightness(${100 - groupingLevel * 3}%)`,
				width: "100%",
				boxSizing: "border-box",
				display: "table-row",
			}}
		>
			<div className="h-stack">
				<div
					className="w-max -my-1 -ml-1"
					onClick={() => {
						row.toggleExpanded();
					}}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name={row.getIsExpanded() ? "ChevronDown" : "ChevronRight"} />}
					/>
				</div>
				<div
					data-pinned-left={true}
					className="flex-1"
				>
					{renderGroupHeader ? (
						renderGroupHeader(row)
					) : (
						<div className="inset-0 absolute stripes" />
					)}
				</div>
			</div>
		</div>
	);
}
